<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { deviceGradeNormalize } from "~/tiw/helpers/deviceGrade";
import { apiGetQuestions } from "~/tiw/hooks/requests/apiGetQuestions/apiGetQuestions";
import useTradeInCalculation from "~/tiw/hooks/useTradeInCalculation/useTradeInCalculation";
import useTranslate from "~/tiw/hooks/useTranslate/useTranslate";
import { useHowYouRateDeviceStore } from "~/tiw/stores/howYouRateDevice/howYouRateDevice";
import { GRADES_DATA } from "~/tiw/stores/howYouRateDevice/howYouRateDevice.model";
import { I_HowYouRateDevice } from "~/tiw/stores/howYouRateDevice/howYouRateDevice.types";
import { useTIWStore } from "~/tiw/stores/tiw/tiw";
import BackButton from "~/tiw/ui/button/backButton/backButton.vue";
import Button from "~/tiw/ui/button/button.vue";
import { RadioSkeleton } from "~/tiw/ui/skeletons";

import Modal from "../../modal/modal.vue";

const HowYouRateDeviceStore = useHowYouRateDeviceStore();

const prevFlow = ref<I_HowYouRateDevice["questionData"][]>([]);
const questionData = ref<I_HowYouRateDevice["questionData"]>(
    HowYouRateDeviceStore.discountLaterFirstQuestion,
);
const answers = ref<string[]>([]);
const isLoading = ref<boolean>(false);

const TIWStore = useTIWStore();

const isMultiSelect = computed(() => {
    return questionData.value.answerType === "MULTISELECT";
});

const isYesNoQuestion = computed(() => {
    return questionData.value.answerType === "YN";
});

watch(answers, () => console.log(answers.value), { deep: true });

const setAnswer = (event: Event) => {
    const target = event.target as HTMLInputElement;

    if (answers.value.includes(target.value)) {
        answers.value = answers.value.filter((el) => el !== target.value);
        return;
    }
    if (isMultiSelect.value) {
        answers.value.push(target.value);
    } else {
        answers.value = [];
        answers.value.push(target.value);
    }
};

function onBack() {
    const prev = prevFlow.value.pop();
    if (!prev) {
        TIWStore.switchStep("howYouRateDevice");
        return;
    }

    questionData.value = prev;
}

const getQuestionData = async (payload: { ans: string; c: string }) => {
    isLoading.value = true;
    if (questionData.value) prevFlow.value.push(questionData.value);

    questionData.value = await apiGetQuestions({
        ...payload,
        deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
        lang: TIWStore.standardizedLocale,
        questionId: questionData.value?.questionId ?? "",
        token: TIWStore.WIDGET_DATA.token,
    });
    isLoading.value = false;
};

async function onNext(yesNoAnswer?: string) {
    const joinedAnswer = answers.value.join();
    if (questionData.value.last) {
        await getQuestionData({ c: joinedAnswer, ans: yesNoAnswer });
        TIWStore.switchStep("successfulEvaluation");
        HowYouRateDeviceStore.grade = deviceGradeNormalize(questionData.value.device.grade);
        HowYouRateDeviceStore.rate = useTranslate(
            `tiw__grades__${GRADES_DATA[questionData.value.device.grade].gradeCode}_stateInfo`,
        );
        useTradeInCalculation({
            tradeInPrice: questionData.value.device.price,
        });
        return;
    }
    await getQuestionData({ c: joinedAnswer, ans: yesNoAnswer });
    answers.value = [];
}
</script>

<template>
    <Modal class="tiw__visualEvaluation">
        <template #header>
            <p class="tiw__h2">
                {{ useTranslate("tiw__visualEvaluation__title") }}
            </p>
        </template>
        <div v-if="questionData" class="tiw__visualEvaluation__question">
            <p class="tiw__visualEvaluation__question__title">
                <b>{{ questionData.question }}</b>
            </p>
            <p class="tiw__visualEvaluation__question__desc" v-html="questionData.description" />
            <div v-if="questionData.options" class="tiw__visualEvaluation__question__options">
                <div
                    v-for="option in questionData.options"
                    :key="option.cid"
                    class="tiw__visualEvaluation__question__options__item"
                >
                    <input
                        :id="option.cid"
                        class="tiw__visualEvaluation__question__options__item__input"
                        :value="option.cid"
                        type="checkbox"
                        :checked="answers.includes(option.cid)"
                        name="options"
                        @change="setAnswer"
                    />
                    <label
                        class="tiw__visualEvaluation__question__options__item__label"
                        :for="option.cid"
                    >
                        <span class="tiw__visualEvaluation__question__options__item__label__cycle">
                            <div
                                class="tiw__visualEvaluation__question__options__item__label__cycle__dot"
                            ></div>
                        </span>
                        <div class="tiw__visualEvaluation__question__options__item__label__info">
                            {{ option.cname }}
                            <div
                                class="tiw__visualEvaluation__question__options__item__label__info__desc"
                                v-html="option.description"
                            />
                        </div>
                    </label>
                </div>
            </div>
            <div class="tiw__visualEvaluation__question__actions">
                <BackButton @click="onBack" />
                <div class="tiw__visualEvaluation__question__actions__yesNo">
                    <Button
                        v-if="isYesNoQuestion"
                        variant="basic"
                        color="light"
                        :disabled="isYesNoQuestion ? isLoading : !answers.length || isLoading"
                        @click="isYesNoQuestion ? onNext('N') : onNext()"
                    >
                        No
                    </Button>
                    <Button
                        :disabled="isYesNoQuestion ? isLoading : !answers.length || isLoading"
                        @click="isYesNoQuestion ? onNext('Y') : onNext()"
                    >
                        {{ isYesNoQuestion ? "Yes" : useTranslate("tiw__continue") }}
                    </Button>
                </div>
            </div>
        </div>
        <div v-else class="tiw__visualEvaluation__skeletons">
            <RadioSkeleton :count="8" />
        </div>
    </Modal>
</template>

<style scoped lang="scss" src="./visualEvaluation.scss"></style>
